import { useState } from "react";
import { Icon } from '@iconify/react';
import BillSeriesModal from "./BillSeriesModal";

const BillSeries = () => {
    const [billSeries, setBillSeries] = useState([
        { id: 1, prefix: "INV", startNumber: 1001, currentNumber: 1045, suffix: "2024", billingType: "Monthly Maintenance" },
        { id: 2, prefix: "WB", startNumber: 1, currentNumber: 156, suffix: "2024", billingType: "Water Charges" },
        { id: 3, prefix: "PK", startNumber: 100, currentNumber: 234, suffix: "2024", billingType: "Parking Charges" },
    ]);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        prefix: "",
        startNumber: "",
        currentNumber: "",
        suffix: "",
        billingType: ""
    });
    const [editId, setEditId] = useState(null);
    const [viewMode, setViewMode] = useState("card"); // "card" or "list"

    const billingTypes = ["Monthly Maintenance", "Water Charges", "Parking Charges", "Sinking Fund", "Repair Fund"];

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editId) {
            setBillSeries(billSeries.map(item =>
                item.id === editId ? { ...formData, id: editId } : item
            ));
        } else {
            setBillSeries([...billSeries, { ...formData, id: Date.now(), currentNumber: formData.startNumber }]);
        }
        handleCloseModal();
    };

    const handleEdit = (item) => {
        setFormData(item);
        setEditId(item.id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this bill series?")) {
            setBillSeries(billSeries.filter(item => item.id !== id));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ prefix: "", startNumber: "", currentNumber: "", suffix: "", billingType: "" });
        setEditId(null);
    };

    const handleSaveAll = () => {
        console.log('Saving all bill series:', billSeries);
        alert('All changes saved successfully!');
    };

    const getBillingTypeColor = (type) => {
        switch (type) {
            case 'Monthly Maintenance': return { bg: '#dcfce7', color: '#16a34a', icon: 'mdi:home-city' };
            case 'Water Charges': return { bg: '#dbeafe', color: '#2563eb', icon: 'mdi:water' };
            case 'Parking Charges': return { bg: '#fef3c7', color: '#d97706', icon: 'mdi:car' };
            case 'Sinking Fund': return { bg: '#fce7f3', color: '#db2777', icon: 'mdi:piggy-bank' };
            case 'Repair Fund': return { bg: '#fee2e2', color: '#dc2626', icon: 'mdi:wrench' };
            default: return { bg: '#f3f4f6', color: '#6b7280', icon: 'mdi:receipt' };
        }
    };

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-base">Bill Series</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Configure bill numbering series for invoices and receipts
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        onClick={handleSaveAll}
                        className="btn d-flex align-items-center gap-2 px-20 py-10 radius-8 text-white"
                        style={{ backgroundColor: '#f59e0b' }}
                    >
                        <Icon icon="mdi:content-save" className="text-lg" />
                        Save Changes
                    </button>
                </div>
            </div>

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:information-outline" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> Bill series define how invoice numbers are generated.
                        Format: PREFIX-NUMBER-SUFFIX (e.g., INV-1001-2024)
                    </p>
                </div>
            </div>

            {/* Action Bar with Add Button and View Toggle */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <button
                    onClick={() => setShowModal(true)}
                    className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                    style={{ backgroundColor: '#f59e0b' }}
                >
                    <Icon icon="mdi:plus-circle" className="text-base" />
                    Add New Bill Series
                </button>

                {/* View Toggle */}
                <div className="btn-group" role="group">
                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('card')}
                    >
                        <Icon icon="mdi:view-grid" className="text-lg" />
                        <span className="d-none d-sm-inline">Card</span>
                    </button>
                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('list')}
                    >
                        <Icon icon="mdi:view-list" className="text-lg" />
                        <span className="d-none d-sm-inline">List</span>
                    </button>
                </div>
            </div>

            {/* Card View */}
            {viewMode === 'card' && (
                <div className="row gy-4">
                    {billSeries.map((item, index) => {
                        const typeStyle = getBillingTypeColor(item.billingType);
                        return (
                            <div key={item.id} className="col-xxl-4 col-lg-6">
                                <div className="card radius-12 border-0 h-100 shadow-sm hover-shadow-lg transition-all">
                                    {/* Card Header */}
                                    <div className="card-header py-16 px-20 bg-base border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="w-44-px h-44-px rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: typeStyle.bg }}
                                            >
                                                <Icon
                                                    icon={typeStyle.icon}
                                                    className="text-2xl"
                                                    style={{ color: typeStyle.color }}
                                                />
                                            </div>
                                            <div>
                                                <span className="badge bg-neutral-100 text-secondary-light text-xs fw-medium px-10 py-4 radius-4">
                                                    S No.: {String(index + 1).padStart(2, '0')}
                                                </span>
                                                <h6 className="fw-semibold mb-0 mt-1">{item.billingType}</h6>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                        >
                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                            <span className="d-none d-xl-inline">Delete</span>
                                        </button>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body p-20">
                                        <div className="row gy-3">
                                            {/* Billing Type */}
                                            <div className="col-12">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Billing Type
                                                </label>
                                                <select
                                                    className="form-select radius-8 h-44-px bg-neutral-50"
                                                    value={item.billingType}
                                                    onChange={(e) => {
                                                        setBillSeries(billSeries.map(bs =>
                                                            bs.id === item.id ? { ...bs, billingType: e.target.value } : bs
                                                        ));
                                                    }}
                                                >
                                                    {billingTypes.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Prefix */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Prefix
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.prefix}
                                                    onChange={(e) => {
                                                        setBillSeries(billSeries.map(bs =>
                                                            bs.id === item.id ? { ...bs, prefix: e.target.value } : bs
                                                        ));
                                                    }}
                                                    placeholder="e.g., INV"
                                                />
                                            </div>

                                            {/* Suffix */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Suffix
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.suffix}
                                                    onChange={(e) => {
                                                        setBillSeries(billSeries.map(bs =>
                                                            bs.id === item.id ? { ...bs, suffix: e.target.value } : bs
                                                        ));
                                                    }}
                                                    placeholder="e.g., 2024"
                                                />
                                            </div>

                                            {/* Start Number */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Start Number
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.startNumber}
                                                    onChange={(e) => {
                                                        setBillSeries(billSeries.map(bs =>
                                                            bs.id === item.id ? { ...bs, startNumber: parseInt(e.target.value) || 0 } : bs
                                                        ));
                                                    }}
                                                    placeholder="e.g., 1001"
                                                    min="1"
                                                />
                                            </div>

                                            {/* Current Number */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Current Number
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.currentNumber}
                                                    onChange={(e) => {
                                                        setBillSeries(billSeries.map(bs =>
                                                            bs.id === item.id ? { ...bs, currentNumber: parseInt(e.target.value) || 0 } : bs
                                                        ));
                                                    }}
                                                    placeholder="e.g., 1045"
                                                    min="1"
                                                />
                                            </div>
                                        </div>

                                        {/* Sample Format Preview */}
                                        <div className="mt-16 p-16 bg-neutral-50 radius-8">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:file-document-outline" className="text-base text-primary-600" />
                                                    <span className="text-xs text-secondary-light">Sample Format</span>
                                                </div>
                                            </div>
                                            <code className="d-block text-center py-8 bg-white radius-4 fw-bold text-lg">
                                                {item.prefix}-{item.currentNumber}{item.suffix ? `-${item.suffix}` : ''}
                                            </code>
                                        </div>

                                        {/* Stats Row */}
                                        <div className="mt-12 p-12 bg-success-50 radius-8">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:counter" className="text-base text-success-600" />
                                                    <span className="text-xs text-secondary-light">Bills Generated</span>
                                                </div>
                                                <span className="fw-bold text-lg text-success-600">
                                                    {item.currentNumber - item.startNumber}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="mt-20 d-flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                            >
                                                <Icon icon="mdi:pencil" className="text-lg" />
                                                <span className="text-sm">Edit Series</span>
                                            </button>
                                            <button className="btn btn-outline-info flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8">
                                                <Icon icon="mdi:restart" className="text-lg" />
                                                <span className="text-sm">Reset Counter</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Empty State */}
                    {billSeries.length === 0 && (
                        <div className="col-12">
                            <div className="card radius-12 border-0 shadow-sm">
                                <div className="card-body p-24 text-center">
                                    <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                        <Icon icon="mdi:numeric" className="text-secondary-light text-2xl" />
                                    </div>
                                    <h6 className="fw-semibold mb-2">No Bill Series Added Yet</h6>
                                    <p className="text-secondary-light text-sm mb-20">
                                        Get started by adding your first bill series to configure invoice numbering.
                                    </p>
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                    >
                                        <Icon icon="mdi:plus-circle" className="text-base" />
                                        Add First Bill Series
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Billing Type</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Prefix</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Suffix</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Current No.</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Sample Format</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billSeries.map((item, index) => {
                                        const typeStyle = getBillingTypeColor(item.billingType);
                                        return (
                                            <tr key={item.id}>
                                                <td className="ps-12 py-8 align-middle">
                                                    <span className="text-secondary-light fw-medium">{String(index + 1).padStart(2, '0')}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="w-32-px h-32-px rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: typeStyle.bg }}
                                                        >
                                                            <Icon
                                                                icon={typeStyle.icon}
                                                                className="text-lg"
                                                                style={{ color: typeStyle.color }}
                                                            />
                                                        </div>
                                                        <span className="fw-medium">{item.billingType}</span>
                                                    </div>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="badge bg-primary-50 text-primary-600 fw-medium px-12 py-6 radius-4">
                                                        {item.prefix}
                                                    </span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">
                                                        {item.suffix || '-'}
                                                    </span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="fw-bold text-success-600">{item.currentNumber}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <code className="bg-neutral-50 px-12 py-6 radius-4">
                                                        {item.prefix}-{item.currentNumber}{item.suffix ? `-${item.suffix}` : ''}
                                                    </code>
                                                </td>
                                                <td className="pe-12 py-8 align-middle text-end">
                                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.id)}
                                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Empty State for List View */}
                        {billSeries.length === 0 && (
                            <div className="p-40 text-center">
                                <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                    <Icon icon="mdi:numeric" className="text-secondary-light text-2xl" />
                                </div>
                                <h6 className="fw-semibold mb-2">No Bill Series Added Yet</h6>
                                <p className="text-secondary-light text-sm mb-20">
                                    Get started by adding your first bill series to configure invoice numbering.
                                </p>
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                >
                                    <Icon icon="mdi:plus-circle" className="text-base" />
                                    Add First Bill Series
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            <BillSeriesModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                billingTypes={billingTypes}
                loading={false}
            />

            {/* Custom Styles */}
            <style>{`
                .hover-shadow-lg:hover {
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
                    transform: translateY(-2px);
                }

                .transition-all {
                    transition: all 0.3s ease;
                }

                .w-32-px { width: 32px; }
                .h-32-px { height: 32px; }
                .w-40-px { width: 40px; }
                .h-40-px { height: 40px; }
                .w-44-px { width: 44px; }
                .h-44-px { height: 44px; }
                .w-80-px { width: 80px; }
                .h-80-px { height: 80px; }

                .card-body .btn {
                    font-weight: 500;
                }

                .table > :not(caption) > * > * {
                    border-bottom-color: var(--bs-border-color);
                }
            `}</style>
        </div>
    );
};

export default BillSeries;
